//react-query hooks to serve the AnnotationService methods

import { useMutation, useQueryClient, useQuery } from 'react-query';
import { AnnotationService } from '../../api/AnnotationService';
import {
  Annotation,
  AnnotationData,
  GeometryData,
  AnnotationType,
  AnnotationStyle,
  AnnotationTransform,
} from '../../api/protosCompiled/annotation/annotation_pb';
import { EnhancedShapeAnnotation, Note } from './types';
import { constants } from './constatnt';
import { UpdateShapeData } from './utils';

// Convert protobuf Annotation to Note type
const convertToNote = (annotation: Annotation.AsObject): Note => {
  const data = annotation.annotationdata;

  // Get text from geometry if available, otherwise fall back to legacy text field
  const getText = () => {
    if (data?.geometry?.text) return data.geometry.text;
    if (data?.text) return data.text;
    return '';
  };

  // Get position from geometry if available, otherwise fall back to legacy fields
  const getX = () => {
    if (data?.geometry?.x) return parseFloat(data.geometry.x);
    if (data?.x) return parseFloat(data.x);
    return 0;
  };

  const getY = () => {
    if (data?.geometry?.y) return parseFloat(data.geometry.y);
    if (data?.y) return parseFloat(data.y);
    return 0;
  };

  return {
    id: annotation.id,
    objectId: annotation.objectid,
    x: getX(),
    y: getY(),
    text: getText(),
    isEditing: false, // Default UI state
    createdAt: annotation.lastupdated ? new Date(annotation.lastupdated).getTime() : Date.now(),
    updatedAt: annotation.lastupdated ? new Date(annotation.lastupdated).getTime() : Date.now(),
    createdByUserId: annotation.createdbyuserid,
    createdByAccountId: annotation.createdbyaccountid,
  };
};

// Convert Note type to protobuf Annotation
const convertToAnnotation = (
  note: Partial<Note> & { id: string; objectId: string }
): Annotation => {
  const annotation = new Annotation();
  annotation.setId(note.id);
  annotation.setObjectid(note.objectId);

  const data = new AnnotationData();
  if (note.id) data.setId(note.id);
  if (note.x !== undefined) data.setX(note.x.toString());
  if (note.y !== undefined) data.setY(note.y.toString());
  if (note.text !== undefined) data.setText(note.text);

  annotation.setAnnotationdata(data);
  return annotation;
};

export const useGetAnnotationsQuery = (objectId: string) => {
  const query = useQuery(
    ['annotations', objectId],
    () => {
      return AnnotationService.getAnnotations(objectId);
    },
    {
      staleTime: 0, // Always consider data stale to force refetch
      cacheTime: 1000 * 60 * 5, // Cache for 5 minutes max
      enabled: !!objectId,
      refetchOnMount: true, // Refetch when component mounts
      refetchOnWindowFocus: true, // Refetch when window gets focus
      onSuccess: data => {},
      onError: error => {
        console.error('useGetAnnotationsQuery: Error fetching annotations:', error);
      },
    }
  );

  // Convert the backend annotations to our frontend Note format (legacy TEXT only)
  // Exclude any newly created proto-based TEXT notes that may have been persisted without geometry
  const annotations = query.data?.annotationsList
    ? query.data.annotationsList
        .filter(a => {
          const t = (a.annotationdata?.type as unknown as number) ?? AnnotationType.TEXT;
          const hasGeometry = !!a.annotationdata?.geometry;
          const customProps: Array<[string, string]> =
            ((a.annotationdata as any)?.custompropertiesMap as Array<[string, string]>) || [];
          const renderProp = customProps.find(([k]) => k === 'render')?.[1];
          const markedAsShapeText = renderProp === 'text-shape';
          // Only render legacy notes that are NOT marked to render as shape-text and have no geometry
          return t === AnnotationType.TEXT && !hasGeometry && !markedAsShapeText;
        })
        .map(convertToNote)
    : [];

  return {
    annotations,
    ...query,
  };
};

// Convert protobuf Annotation to EnhancedShapeAnnotation
const convertToEnhancedShapeAnnotation = (
  ann: Annotation.AsObject
): EnhancedShapeAnnotation | null => {
  const data = ann.annotationdata;
  if (!data) return null;

  // All annotation types with proper geometry are considered shapes
  const type = (data.type as unknown as number) ?? AnnotationType.TEXT;
  // Check custom render hint from custompropertiesMap
  const customProps: Array<[string, string]> =
    ((data as any)?.custompropertiesMap as Array<[string, string]>) || [];
  const renderProp = customProps.find(([k]) => k === 'render')?.[1];
  const markedAsShapeText = renderProp === 'text-shape';

  // Check if this is a legacy text annotation (no geometry) vs new proto-based text annotation
  if (type === AnnotationType.TEXT && !data.geometry && !markedAsShapeText) {
    // Legacy text annotation - let it be handled by the old Note system
    return null;
  }

  // Position fallback
  const getX = () => {
    if (data.geometry?.x) return parseFloat(data.geometry.x);
    if (data.x) return parseFloat(data.x);
    return 0;
  };
  const getY = () => {
    if (data.geometry?.y) return parseFloat(data.geometry.y);
    if (data.y) return parseFloat(data.y);
    return 0;
  };

  // Extract style information from the annotation data
  const extractStyle = () => {
    if (!data.style) {
      // If style is missing, attempt to read from custom properties fallbacks
      const customProps: Array<[string, string]> =
        ((data as any)?.custompropertiesMap as Array<[string, string]>) || [];
      const getProp = (key: string, fallback: string) => {
        const found = customProps.find(([k]) => k === key)?.[1];
        return found || fallback;
      };
      return {
        fill: constants.shape.fill,
        stroke: constants.shape.stroke,
        strokeWidth: constants.shape.strokeWidth,
        strokeDasharray: constants.shape.strokeDasharray,
        opacity: constants.shape.opacity,
        fontSize: constants.shape.fontSize,
        fontFamily: constants.shape.fontFamily,
        fontWeight: constants.shape.fontWeight,
        fontStyle: getProp('fontStyle', constants.shape.fontStyle),
        textDecoration: getProp('textDecoration', constants.shape.textDecoration),
        textAnchor: constants.shape.textAnchor,
      } as any;
    }

    const style = data.style;
    const extractedStyle = {
      fill: style.fill || constants.shape.fill,
      stroke: style.stroke || constants.shape.stroke,
      strokeWidth: style.strokewidth || constants.shape.strokeWidth,
      strokeDasharray: style.strokedasharray || constants.shape.strokeDasharray,
      opacity: style.opacity || constants.shape.opacity,
      fontSize: style.fontsize || constants.shape.fontSize,
      fontFamily: style.fontfamily || constants.shape.fontFamily,
      fontWeight: style.fontweight || constants.shape.fontWeight,
      // Support both protobuf lowercase and camelCase keys (from optimistic updates)
      fontStyle: (style as any).fontstyle || (style as any).fontStyle || constants.shape.fontStyle,
      textDecoration:
        (style as any).textdecoration ||
        (style as any).textDecoration ||
        constants.shape.textDecoration,
      textAnchor: style.textanchor || constants.shape.textAnchor,
    };

    // Also merge possible custom property fallbacks
    try {
      const customProps: Array<[string, string]> =
        ((data as any)?.custompropertiesMap as Array<[string, string]>) || [];
      const getProp = (key: string) => customProps.find(([k]) => k === key)?.[1];
      const fontStyle = getProp('fontStyle');
      const textDecoration = getProp('textDecoration');
      return {
        ...extractedStyle,
        ...(fontStyle ? { fontStyle } : {}),
        ...(textDecoration ? { textDecoration } : {}),
      } as any;
    } catch {
      return extractedStyle as any;
    }
  };

  return {
    id: ann.id,
    objectId: ann.objectid,
    createdByUserId: ann.createdbyuserid,
    createdByAccountId: ann.createdbyaccountid,
    lastUpdated: ann.lastupdated || '',
    type: type as AnnotationType,
    geometry: data.geometry,
    style: extractStyle(),
    transform: data.transform,
    layerId: (data as any).layerid || 'default',
    zIndex: (() => {
      try {
        const customProps: Array<[string, string]> =
          ((data as any)?.custompropertiesMap as Array<[string, string]>) || [];
        const z = customProps.find(([k]) => k === 'zIndex')?.[1];
        const parsed = z !== undefined ? parseInt(z, 10) : 0;
        return Number.isFinite(parsed) ? parsed : 0;
      } catch {
        return 0;
      }
    })(),
    x: getX(),
    y: getY(),
    text: data.geometry?.text || data.text || '',
    isdimensional: data.isdimensional || false,
  };
};

// Fetch only shape annotations (non-text) with full geometry/type
export const useGetShapeAnnotations = (objectId: string) => {
  const query = useQuery(
    ['annotations', objectId],
    () => AnnotationService.getAnnotations(objectId),
    {
      enabled: !!objectId,
      staleTime: 0,
      cacheTime: 1000 * 60 * 5,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );

  const annotations = query.data?.annotationsList
    ? query.data.annotationsList
        .map((annotation, index) => {
          return convertToEnhancedShapeAnnotation(annotation);
        })
        .filter((a): a is EnhancedShapeAnnotation => !!a)
        .sort((a, b) => {
          const layerA = (a.layerId || 'default').localeCompare(b.layerId || 'default');
          if (layerA !== 0) return layerA;
          const za = a.zIndex ?? 0;
          const zb = b.zIndex ?? 0;
          return za - zb;
        })
    : [];

  return { annotations, ...query };
};

export const useCreateAnnotationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (annotationData: any) => {
      // Create protobuf annotation from enhanced data
      const annotation = new Annotation();
      const id =
        annotationData.id || `annotation-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      annotation.setId(id);
      annotation.setObjectid(annotationData.objectid);

      const data = new AnnotationData();
      data.setId(id);
      if (annotationData.annotationdata?.type !== undefined)
        data.setType(annotationData.annotationdata.type);
      if (annotationData.annotationdata?.geometry) {
        const geometry = new GeometryData();
        const geomInput = annotationData.annotationdata.geometry as any;
        Object.keys(geomInput).forEach(key => {
          const value = geomInput[key];
          if (value === undefined || value === null) return;
          if (key === 'hyperlinkdata') {
            try {
              const HyperlinkData = require('../../api/protosCompiled/annotation/annotation_pb')
                .HyperlinkData as any;
              const link = new HyperlinkData();
              // Type guard to ensure value is an object with the expected properties
              if (typeof value === 'object' && value !== null) {
                const hyperlinkData = value as any;
                if (hyperlinkData.targettype !== undefined)
                  link.setTargettype(hyperlinkData.targettype);
                if (hyperlinkData.targetobjectid !== undefined)
                  link.setTargetobjectid(String(hyperlinkData.targetobjectid));
              }
              (geometry as any).setHyperlinkdata(link);
            } catch {}
            return;
          }
          if (key === 'leaders' || key === 'leadersList') {
            try {
              const pb = require('../../api/protosCompiled/annotation/annotation_pb');
              const Leader = pb.Leader as any;
              const LeaderPoint = pb.LeaderPoint as any;
              const LeaderStyle = pb.LeaderStyle as any;
              const list = Array.isArray(value) ? value : value?.leadersList || [];
              const leaders = list.map((raw: any) => {
                const l = new Leader();
                if (raw.anchor !== undefined) l.setAnchor(raw.anchor);
                if (raw.pivot) {
                  const p = new LeaderPoint();
                  if (raw.pivot.x !== undefined) p.setX(String(raw.pivot.x));
                  if (raw.pivot.y !== undefined) p.setY(String(raw.pivot.y));
                  l.setPivot(p);
                }
                if (raw.end) {
                  const e = new LeaderPoint();
                  if (raw.end.x !== undefined) e.setX(String(raw.end.x));
                  if (raw.end.y !== undefined) e.setY(String(raw.end.y));
                  l.setEnd(e);
                }
                if ((raw as any).showarrowhead !== undefined)
                  l.setShowarrowhead(!!(raw as any).showarrowhead);
                if ((raw as any).arrowheadsize !== undefined)
                  l.setArrowheadsize(String((raw as any).arrowheadsize));
                if (raw.style) {
                  const s = new LeaderStyle();
                  if (raw.style.stroke !== undefined) s.setStroke(String(raw.style.stroke));
                  if (raw.style.strokewidth !== undefined)
                    s.setStrokewidth(String(raw.style.strokewidth));
                  if (raw.style.strokedasharray !== undefined)
                    s.setStrokedasharray(String(raw.style.strokedasharray));
                  if (raw.style.opacity !== undefined) s.setOpacity(String(raw.style.opacity));
                  l.setStyle(s);
                }
                return l;
              });
              (geometry as any).setLeadersList(leaders);
            } catch {}
            return;
          }
          const methodName = `set${key.charAt(0).toUpperCase() + key.slice(1)}`;
          if (typeof (geometry as any)[methodName] === 'function') {
            (geometry as any)[methodName](value);
          }
        });
        data.setGeometry(geometry);
      }

      // Set style information if provided
      if (annotationData.annotationdata?.style) {
        const style = new AnnotationStyle();
        const styleData = annotationData.annotationdata.style;

        // Map style properties to protobuf format
        if (styleData.fill !== undefined) style.setFill(styleData.fill);
        if (styleData.stroke !== undefined) style.setStroke(styleData.stroke);
        if (styleData.strokeWidth !== undefined) style.setStrokewidth(styleData.strokeWidth);
        if (styleData.strokeDasharray !== undefined)
          style.setStrokedasharray(styleData.strokeDasharray);
        if (styleData.opacity !== undefined) style.setOpacity(styleData.opacity);
        if (styleData.fontSize !== undefined) style.setFontsize(styleData.fontSize);
        if (styleData.fontFamily !== undefined) style.setFontfamily(styleData.fontFamily);
        if (styleData.fontWeight !== undefined) style.setFontweight(styleData.fontWeight);
        if (styleData.textAnchor !== undefined) style.setTextanchor(styleData.textAnchor);

        data.setStyle(style);
      }

      // Persist extra text styles not present in proto style via custom properties
      try {
        const styleData = annotationData.annotationdata?.style || {};
        const map = (data as any).getCustompropertiesMap?.();
        if (map && typeof map.set === 'function') {
          if (styleData.fontStyle !== undefined) map.set('fontStyle', String(styleData.fontStyle));
          if (styleData.textDecoration !== undefined)
            map.set('textDecoration', String(styleData.textDecoration));
        }
      } catch {}

      // Set backward compatibility fields
      if (annotationData.annotationdata?.x !== undefined)
        data.setX(annotationData.annotationdata.x);
      if (annotationData.annotationdata?.y !== undefined)
        data.setY(annotationData.annotationdata.y);
      if (annotationData.annotationdata?.text !== undefined)
        data.setText(annotationData.annotationdata.text);
      if (annotationData.annotationdata?.isdimensional !== undefined)
        data.setIsdimensional(annotationData.annotationdata.isdimensional);

      // Set layer: default to 'default' if not provided
      const layerIdToSet =
        (annotationData.annotationdata && (annotationData.annotationdata as any).layerid) ||
        'default';
      (data as any).setLayerid?.(layerIdToSet);

      // Persist zIndex in custom properties (default 0)
      try {
        const map = (data as any).getCustompropertiesMap?.();
        const providedZ = (annotationData.annotationdata as any)?.zIndex;
        const z = providedZ !== undefined ? providedZ : 0;
        if (map && typeof map.set === 'function') map.set('zIndex', String(z));
      } catch {}

      // Set a human-readable type name into customProperties for consumers that expect a string
      try {
        const typeNum = (annotationData.annotationdata?.type as unknown as number) ?? undefined;
        if (typeNum !== undefined) {
          const typeName = AnnotationType[typeNum] || '';
          const map = (data as any).getCustompropertiesMap?.();
          if (map && typeof map.set === 'function') {
            map.set('typeName', String(typeName).toLowerCase());
          }
        }
      } catch {}

      annotation.setAnnotationdata(data);
      return AnnotationService.createAnnotation(annotation);
    },
    {
      // After the mutation, invalidate the related query to refetch from server
      onSuccess: (result, variables, context) => {
        // Remove the optimistic shape immediately when the server responds
        // This prevents duplicates from appearing on screen
        const store = (require('./annotationStore') as any).useAnnotationStore?.getState?.();
        if (store?.removeOptimisticShape) {
          // Find and remove the optimistic shape by matching the temp ID
          // The temp ID is stored in the id field of the annotationData
          const tempId = variables.id;
          if (tempId) {
            store.removeOptimisticShape(tempId);
          }
        }

        // If the server returned the annotation, we can use it to update the cache
        // This ensures the annotation is visible and properly styled
        if (result?.annotation) {
          const annotation = result.annotation;
          // Update the cache with the server response to ensure proper data
          // Use the same query key as shape annotations for consistency
          queryClient.setQueryData(['annotations', variables.objectid], (old: any) => {
            if (!old) return { annotationsList: [annotation] };

            // Check if this annotation already exists
            const existingIndex = old.annotationsList.findIndex((a: any) => a.id === annotation.id);

            if (existingIndex >= 0) {
              // Update existing annotation
              const updatedList = [...old.annotationsList];
              updatedList[existingIndex] = annotation;
              return { ...old, annotationsList: updatedList };
            } else {
              // Add new annotation
              return {
                ...old,
                annotationsList: [...old.annotationsList, annotation],
              };
            }
          });
        }

        // Invalidate and refetch to get the real annotation from server
        // Use the same query key as shape annotations for consistency
        queryClient.invalidateQueries(['annotations', variables.objectid]);
      },
      onError: (error, variables) => {
        console.error('useCreateAnnotationMutation: Error creating annotation:', error);
      },
    }
  );
};

export const useUpdateAnnotationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (data: { note: Partial<Note>; objectId: string }) => {
      const annotation = convertToAnnotation({
        ...data.note,
        id: data.note.id || '', // Add fallback to prevent undefined
        objectId: data.objectId,
      });
      return AnnotationService.updateAnnotation(annotation);
    },
    {
      // Optimistic update
      onMutate: async newData => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(['annotations', newData.objectId]);

        // Snapshot previous value
        const previousData = queryClient.getQueryData(['annotations', newData.objectId]);

        // Optimistically update
        queryClient.setQueryData(['annotations', newData.objectId], (old: any) => {
          if (!old?.annotationsList) return old;

          const updatedList = old.annotationsList.map((annotation: any) => {
            if (annotation.id === newData.note.id) {
              // Update the annotation data
              return {
                ...annotation,
                annotationdata: {
                  ...annotation.annotationdata,
                  x:
                    newData.note.x !== undefined
                      ? newData.note.x.toString()
                      : annotation.annotationdata.x,
                  y:
                    newData.note.y !== undefined
                      ? newData.note.y.toString()
                      : annotation.annotationdata.y,
                  text:
                    newData.note.text !== undefined
                      ? newData.note.text
                      : annotation.annotationdata.text,
                },
                lastupdated: Date.now(),
              };
            }
            return annotation;
          });

          return {
            ...old,
            annotationsList: updatedList,
          };
        });

        return { previousData };
      },

      // If the mutation fails, roll back
      onError: (err, newData, context: any) => {
        queryClient.setQueryData(['annotations', newData.objectId], context.previousData);
      },

      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(['annotations', variables.objectId]);
      },
    }
  );
};

export const useDeleteAnnotationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: { id: string; objectId: string }) => {
      return AnnotationService.deleteAnnotation(data.id);
    },
    {
      // Optimistic update
      onMutate: async newData => {
        // Cancel any outgoing refetches for both query types
        await queryClient.cancelQueries(['annotations', newData.objectId]);

        // Snapshot previous values for both query types
        const previousAnnotations = queryClient.getQueryData(['annotations', newData.objectId]);

        // Optimistically update annotations query
        queryClient.setQueryData(['annotations', newData.objectId], (old: any) => {
          if (!old?.annotationsList) return old;

          return {
            ...old,
            annotationsList: old.annotationsList.filter(
              (annotation: any) => annotation.id !== newData.id
            ),
          };
        });

        // Optimistically update annotations query
        queryClient.setQueryData(['annotations', newData.objectId], (old: any) => {
          if (!old?.annotationsList) return old;

          return {
            ...old,
            annotationsList: old.annotationsList.filter(
              (annotation: any) => annotation.id !== newData.id
            ),
          };
        });

        return { previousAnnotations };
      },

      // If the mutation fails, roll back
      onError: (err, newData, context: any) => {
        queryClient.setQueryData(['annotations', newData.objectId], context.previousAnnotations);
      },

      onSuccess: (_, variables) => {
        // Invalidate both query types to ensure UI is updated
        queryClient.invalidateQueries(['annotations', variables.objectId]);
      },
    }
  );
};

// Update mutation for shape annotations (non-text) with full geometry
export const useUpdateShapeAnnotationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: UpdateShapeData) => {
      const ann = new Annotation();
      ann.setId(data.id);
      ann.setObjectid(data.objectId);

      const ad = new AnnotationData();
      ad.setId(data.id);
      ad.setType(data.type ?? AnnotationType.TEXT); // Default to TEXT if undefined
      const geom = new GeometryData();
      Object.entries(data.geometry || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null) {
          if (k === 'hyperlinkdata') {
            try {
              const HyperlinkData = require('../../api/protosCompiled/annotation/annotation_pb')
                .HyperlinkData as any;
              const link = new HyperlinkData();
              // Type guard to ensure v is an object with the expected properties
              if (typeof v === 'object' && v !== null) {
                const hyperlinkData = v as any;
                if (hyperlinkData.targettype !== undefined)
                  link.setTargettype(hyperlinkData.targettype);
                if (hyperlinkData.targetobjectid !== undefined)
                  link.setTargetobjectid(String(hyperlinkData.targetobjectid));
              }
              (geom as any).setHyperlinkdata(link);
            } catch (err) {
              console.error('Failed to set hyperlinkdata in update:', err);
            }
            return;
          }
          if (k === 'leaders' || k === 'leadersList') {
            try {
              const pb = require('../../api/protosCompiled/annotation/annotation_pb');
              const Leader = pb.Leader as any;
              const LeaderPoint = pb.LeaderPoint as any;
              const LeaderStyle = pb.LeaderStyle as any;
              const list = Array.isArray(v) ? v : (v as any)?.leadersList || [];
              const leaders = list.map((raw: any) => {
                const l = new Leader();
                if (raw.anchor !== undefined) l.setAnchor(raw.anchor);
                if ((raw as any).pivot) {
                  const p = new LeaderPoint();
                  const piv = (raw as any).pivot;
                  if (piv.x !== undefined) p.setX(String(piv.x));
                  if (piv.y !== undefined) p.setY(String(piv.y));
                  l.setPivot(p);
                }
                if ((raw as any).end) {
                  const e = new LeaderPoint();
                  const end = (raw as any).end;
                  if (end.x !== undefined) e.setX(String(end.x));
                  if (end.y !== undefined) e.setY(String(end.y));
                  l.setEnd(e);
                }
                if ((raw as any).showarrowhead !== undefined)
                  l.setShowarrowhead(!!(raw as any).showarrowhead);
                if ((raw as any).arrowheadsize !== undefined)
                  l.setArrowheadsize(String((raw as any).arrowheadsize));
                if ((raw as any).style) {
                  const s = new LeaderStyle();
                  const st = (raw as any).style;
                  if (st.stroke !== undefined) s.setStroke(String(st.stroke));
                  if (st.strokewidth !== undefined) s.setStrokewidth(String(st.strokewidth));
                  if (st.strokedasharray !== undefined)
                    s.setStrokedasharray(String(st.strokedasharray));
                  if (st.opacity !== undefined) s.setOpacity(String(st.opacity));
                  l.setStyle(s);
                }
                return l;
              });
              (geom as any).setLeadersList(leaders);
            } catch (err) {
              console.error('Failed to set leaders in update:', err);
            }
            return;
          }
          const method = `set${k.charAt(0).toUpperCase()}${k.slice(1)}`;
          if (typeof (geom as any)[method] === 'function') {
            (geom as any)[method](v as any);
          }
        }
      });
      ad.setGeometry(geom);

      // Ensure backward compatibility: if geometry has text, also set it at the root level
      if (data.geometry?.text !== undefined) {
        ad.setText(data.geometry.text);
      }

      // Set isdimensional property if provided
      if (data.isdimensional !== undefined) {
        ad.setIsdimensional(data.isdimensional);
      }

      // Handle style if provided
      if (data.style) {
        const style = new AnnotationStyle();

        // Map style properties to the correct protobuf method names
        const styleMapping: Record<string, string> = {
          fill: 'setFill',
          stroke: 'setStroke',
          strokeWidth: 'setStrokewidth',
          strokeDasharray: 'setStrokedasharray',
          opacity: 'setOpacity',
          fontSize: 'setFontsize',
          fontFamily: 'setFontfamily',
          fontWeight: 'setFontweight',
          fontStyle: 'setFontstyle',
          textDecoration: 'setTextdecoration',
          textAnchor: 'setTextanchor',
        };

        Object.entries(data.style).forEach(([k, v]) => {
          if (v !== undefined && v !== null) {
            const methodName = styleMapping[k];
            if (methodName && typeof (style as any)[methodName] === 'function') {
              (style as any)[methodName](v as any);
            } else {
              console.warn(`⚠️ Unknown style property or method not found: ${k} -> ${methodName}`);
            }
          }
        });

        ad.setStyle(style);
      }

      // Handle transform if provided
      if (data.transform) {
        const transform = new AnnotationTransform();
        Object.entries(data.transform).forEach(([k, v]) => {
          if (v !== undefined && v !== null) {
            const method = `set${k.charAt(0).toUpperCase()}${k.slice(1)}`;
            if (typeof (transform as any)[method] === 'function') {
              (transform as any)[method](v as any);
            }
          }
        });
        ad.setTransform(transform);
      }

      // Persist layer id if provided
      if (data.layerId !== undefined) {
        (ad as any).setLayerid?.(data.layerId || 'default');
      }

      // Persist layer id if provided
      if ((data as any).layerId !== undefined) {
        (ad as any).setLayerid?.((data as any).layerId || 'default');
      }

      ann.setAnnotationdata(ad);

      // Persist extra text styles not present in proto style via custom properties
      try {
        const styleData = data.style || {};
        const map = (ad as any).getCustompropertiesMap?.();
        if (map && typeof map.set === 'function') {
          if (styleData.fontStyle !== undefined) map.set('fontStyle', String(styleData.fontStyle));
          if (styleData.textDecoration !== undefined)
            map.set('textDecoration', String(styleData.textDecoration));
          if (data.zIndex !== undefined) map.set('zIndex', String(data.zIndex));
        }
      } catch {}

      return AnnotationService.updateAnnotation(ann);
    },
    {
      onMutate: async variables => {
        await queryClient.cancelQueries(['annotations', variables.objectId]);
        const previous = queryClient.getQueryData(['annotations', variables.objectId]);
        queryClient.setQueryData(['annotations', variables.objectId], (old: any) => {
          if (!old?.annotationsList && !old?.annotations) return old;
          const list = (old.annotationsList || old.annotations || []).map((a: any) => {
            if (a.id === variables.id) {
              // Ensure text is explicitly preserved for TEXT annotations
              const updatedGeometry = {
                ...(a.annotationdata?.geometry || {}),
                ...variables.geometry,
              };
              if (variables.type === 0 && variables.geometry?.text !== undefined) {
                // AnnotationType.TEXT = 0
                updatedGeometry.text = variables.geometry.text;
              }

              // Update style if provided
              const updatedStyle = variables.style
                ? { ...(a.annotationdata?.style || {}), ...variables.style }
                : a.annotationdata?.style;

              // Update transform if provided
              const updatedTransform = variables.transform
                ? { ...(a.annotationdata?.transform || {}), ...variables.transform }
                : a.annotationdata?.transform;

              // Also update the flattened style property for EnhancedShapeAnnotation compatibility
              const flattenedStyle = variables.style
                ? { ...(a.style || {}), ...variables.style }
                : a.style;

              // Update layerId and zIndex optimistically
              const updatedAnnotationData: any = {
                ...a.annotationdata,
                type: variables.type,
                geometry: updatedGeometry,
                style: updatedStyle,
                transform: updatedTransform,
              };
              if (variables.layerId !== undefined) {
                updatedAnnotationData.layerid = variables.layerId || 'default';
              }
              // Update custompropertiesMap array for zIndex - PRESERVE existing
              let updatedCustomProps: Array<[string, string]> = (
                (a.annotationdata?.custompropertiesMap as Array<[string, string]>) || []
              ).map(([k, v]) => [k, v]);
              if (variables.zIndex !== undefined) {
                const zi = String(variables.zIndex);
                const found = updatedCustomProps.findIndex(([k]) => k === 'zIndex');
                if (found >= 0) updatedCustomProps[found][1] = zi;
                else updatedCustomProps = [...updatedCustomProps, ['zIndex', zi]];
              }
              updatedAnnotationData.custompropertiesMap = updatedCustomProps;

              return {
                ...a,
                // Update the flattened style property
                style: flattenedStyle,
                annotationdata: updatedAnnotationData,
              };
            }
            return a;
          });
          return { ...(old || {}), annotationsList: list };
        });
        return { previous };
      },
      onError: (_err, variables, context: any) => {
        queryClient.setQueryData(['annotations', variables.objectId], context?.previous);
      },
      onSuccess: (_res, variables) => {
        queryClient.invalidateQueries(['annotations', variables.objectId]);
      },
    }
  );
};
